"use client";

import React, { useState } from "react";
import { ArrowRight, Camera, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
const services = [{
  id: "in-home",
  title: "In-Home Intimacy",
  shortDesc: "A private, comfortable exploration of self within the sanctuary of your own space.",
  bgClass: "bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1614369/img/in-home_service_bg.png)]"
}, {
  id: "studio",
  title: "The Studio Suite",
  shortDesc: "Controlled lighting and minimalist editorial backdrops designed for high-fashion impact.",
  bgClass: "bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1614369/img/studio_service_bg.png)]"
}, {
  id: "hotel",
  title: "Elite Hotel Experience",
  shortDesc: "Luxury surroundings in the city's finest suites for a sophisticated, cinematic narrative.",
  bgClass: "bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1614369/img/hotel_service_bg.png)]"
}, {
  id: "bespoke",
  title: "Bespoke Noir",
  shortDesc: "Custom-crafted visual concepts for those seeking an entirely unique and artistic portfolio.",
  bgClass: "bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1614369/img/bespoke_service_bg.png)]"
}];
const packages = [{
  serviceId: "in-home",
  name: "The Sanctuary",
  price: "$750",
  details: "90-minute session in your home. Includes 15 high-end retouched images and professional posing guidance."
}, {
  serviceId: "in-home",
  name: "Intimate Narrative",
  price: "$1,500",
  details: "Half-day documentary session. Includes 40 retouched images, styling consultation, and a premium print box."
}, {
  serviceId: "studio",
  name: "The Signature",
  price: "$950",
  details: "2-hour studio session with two look changes. Includes 20 retouched images and access to the studio wardrobe."
}, {
  serviceId: "studio",
  name: "Editorial Collection",
  price: "$2,200",
  details: "Full-day editorial production. Includes 50+ retouched images, professional hair & makeup, and a custom photo book."
}, {
  serviceId: "hotel",
  name: "The Suite Life",
  price: "$1,800",
  details: "3-hour session in a curated luxury suite. Includes 30 retouched images, champagne service, and full styling."
}, {
  serviceId: "hotel",
  name: "Midnight In Minneapolis",
  price: "$3,500",
  details: "Overnight luxury experience. Extensive evening and morning sessions, 75+ images, and cinematic color grading."
}, {
  serviceId: "bespoke",
  name: "The XO Experience",
  price: "Custom",
  details: "Concept development, multi-location shooting, and advanced fine-art retouching for a one-of-a-kind collection."
}, {
  serviceId: "bespoke",
  name: "Artisan Portfolio",
  price: "Custom",
  details: "A highly curated collection of gallery-grade prints designed for large-format display and private exhibitions."
}];
export default function ServiceExperiences() {
  const [hoveredService, setHoveredService] = useState<string | null>(null);
  return <section data-section-id="151640" id="luxury-session-offerings" className="dark bg-background text-foreground relative min-h-screen flex flex-col justify-center py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 z-0 pointer-events-none bg-background">
        {services.map((service, i) => <div key={service.id} data-index={i} className={cn("absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-in-out", service.bgClass, hoveredService === service.id ? "opacity-20" : "opacity-0")} />)}
        {}
        <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/60 to-background/95" />
      </div>

      {}
      <div className="container relative z-10 mx-auto px-4 md:px-8 lg:px-12">
        <div className="mb-16 md:mb-24 max-w-3xl">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="size-5 text-primary" />
            <span className="text-sm font-sans tracking-widest uppercase text-muted-foreground">
              Luxury Session Offerings
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-serif font-medium leading-tight text-foreground">
            Empowering visual narratives designed to celebrate your essence and capture your most intimate moments.
          </h2>
        </div>

        <div className="flex flex-col w-full">
          {services.map((service, i) => <div key={service.id} data-index={i}>
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" className="group flex items-center justify-between py-8 md:py-12 px-0 border-b border-border/20 text-left w-full transition-all duration-500 hover:border-primary/50 hover:bg-transparent rounded-none focus-visible:ring-offset-transparent h-auto" onMouseEnter={() => setHoveredService(service.id)} onMouseLeave={() => setHoveredService(null)} aria-label={`View details for ${service.title}`}>
                    <span className={cn("text-4xl md:text-6xl lg:text-7xl font-serif font-bold tracking-tighter transition-all duration-500", hoveredService && hoveredService !== service.id ? "text-muted-foreground/20" : "text-foreground")}>
                      {service.title}
                    </span>
                    <div className="overflow-hidden flex items-center justify-center size-12 md:size-16 rounded-full border border-transparent group-hover:border-primary/30 transition-all duration-500 flex-shrink-0">
                      <ArrowRight className={cn("size-8 md:size-10 transition-all duration-500 transform", hoveredService === service.id ? "opacity-100 translate-x-0 text-primary" : "opacity-0 -translate-x-full text-muted-foreground")} />
                    </div>
                  </Button>
                </SheetTrigger>
                
                <SheetContent className="dark bg-card text-card-foreground border-l border-border/20 sm:max-w-lg w-full overflow-y-auto flex flex-col">
                  <SheetHeader className="mb-8 text-left">
                    <div className="size-12 rounded-none bg-primary/10 flex items-center justify-center mb-6 border border-primary/20">
                      <Camera className="size-6 text-primary" />
                    </div>
                    <SheetTitle className="text-3xl font-serif font-bold tracking-tight mb-2 text-foreground">
                      {service.title}
                    </SheetTitle>
                    <SheetDescription className="text-base text-muted-foreground font-sans">
                      {service.shortDesc}
                    </SheetDescription>
                  </SheetHeader>

                  <div className="flex-1 flex flex-col gap-6">
                    <h3 className="text-xs font-sans tracking-widest uppercase text-muted-foreground border-b border-border/20 pb-2">
                      Bespoke Packages
                    </h3>
                    
                    <div className="flex flex-col gap-6">
                      {packages.filter(pkg => pkg.serviceId === service.id).map((pkg, pkgIndex) => <div key={pkg.name} data-index={pkgIndex} className="group/pkg flex flex-col gap-2 p-4 rounded-none border border-border/20 bg-background/50 hover:bg-background transition-colors">
                            <div className="flex items-start justify-between gap-4">
                              <h4 className="text-lg font-serif font-semibold text-foreground">
                                {pkg.name}
                              </h4>
                              <span className="text-sm font-sans bg-primary text-primary-foreground px-3 py-1 rounded-none whitespace-nowrap">
                                {pkg.price}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground leading-relaxed font-sans">
                              {pkg.details}
                            </p>
                          </div>)}
                    </div>
                  </div>

                  <SheetFooter className="mt-12 pt-6 border-t border-border/20 sm:justify-start">
                    <Button asChild size="lg" className="w-full sm:w-auto rounded-none bg-primary text-primary-foreground hover:bg-primary/90">
                      <Link to="/HomePage">
                        Inquire About This Session
                        <ArrowRight className="ml-2 size-4" />
                      </Link>
                    </Button>
                  </SheetFooter>
                </SheetContent>
              </Sheet>
            </div>)}
        </div>
      </div>
    </section>;
}